import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getDressingChangeFrequency, updateDressingChangeFrequency } from "../../../../../../app/patientSlice";
import { useReadOnly } from "../../../../../../hooks/useReadOnly";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useLoading } from "../../../../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../../../../constants/loadingKeys";
import { getDefaultSchedule, scheduleToApiFields } from "../utils/scheduleUtils";
import type {
  Schedule,
  SchedulePatch,
  DayOfWeek,
  DressChangeFrequency,
  UseDressingChangeFrequencyDesignReturn,
} from "../types";

interface HookParams {
  interventionId: string | number;
}

interface RootState {
  patient: {
    dressChangefrequency: DressChangeFrequency;
  };
}

const scheduleKey = (type: "primary" | "secondary", index: number): string =>
  `${type}_${index}`;

const buildSchedules = (dressChange: DressChangeFrequency): Record<string, Schedule> => {
  const next: Record<string, Schedule> = {};
  (["primary", "secondary"] as const).forEach((type) => {
    const items = dressChange?.data?.[type]?.productCategory ?? [];
    items.forEach((item, index) => {
      next[scheduleKey(type, index)] = getDefaultSchedule(item);
    });
  });
  return next;
};

export const useDressingChangeFrequencyDesign = ({
  interventionId,
}: HookParams): UseDressingChangeFrequencyDesignReturn => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams<{ patientId: string; assessmentId: string }>();
  const { handleSubmit } = useForm();
  const isReadOnly = useReadOnly();
  const { hasPermission } = usePermissions();
  const isLoading = useLoading(LOADING_KEYS.GET_DRESSING_CHANGE_FREQUENCY);
  const isSaving = useLoading(LOADING_KEYS.UPDATE_DRESSING_CHANGE_FREQUENCY);

  const dressChange = useSelector((state: RootState) => state.patient.dressChangefrequency);

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [schedules, setSchedules] = useState<Record<string, Schedule>>(() =>
    buildSchedules(dressChange)
  );

  const confirmationClose = useCallback((): void => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback((): void => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
    setSchedules(buildSchedules(dressChange));
  }, [dressChange]);

  const handleEdit = useCallback((): void => {
    setFormChangesConfirm(false);
    setClickCancel(false);
    setEditable(true);
  }, []);

  const handleCancel = useCallback((): void => {
    toast.dismiss("success1");
    setClickCancel(true);
  }, []);

  const updateSchedule = useCallback(
    (type: "primary" | "secondary", index: number, patch: SchedulePatch): void => {
      setFormChangesConfirm(true);
      setSchedules((prev) => ({
        ...prev,
        [scheduleKey(type, index)]: { ...prev[scheduleKey(type, index)], ...patch },
      }));
    },
    []
  );

  const toggleWeekday = useCallback(
    (type: "primary" | "secondary", index: number, day: DayOfWeek): void => {
      const current = schedules[scheduleKey(type, index)];
      if (!current) return;
      const days = current.days.includes(day)
        ? current.days.filter((d) => d !== day)
        : ([...current.days, day] as DayOfWeek[]);
      updateSchedule(type, index, { days });
    },
    [schedules, updateSchedule]
  );

  const getSchedule = useCallback(
    (type: "primary" | "secondary", index: number): Schedule =>
      schedules[scheduleKey(type, index)] ?? getDefaultSchedule({}),
    [schedules]
  );

  const doSubmit = useCallback(async (): Promise<void> => {
    const buildType = (type: "primary" | "secondary") => {
      const items = dressChange?.data?.[type]?.productCategory ?? [];
      const productCategory = items.map((item, index) => {
        const schedule = schedules[scheduleKey(type, index)] ?? getDefaultSchedule(item);
        const apiFields = scheduleToApiFields(schedule);
        return {
          ...item,
          ...apiFields,
        };
      });
      return { productCategory };
    };

    const updateData = {
      data: {
        primary: buildType("primary"),
        secondary: buildType("secondary"),
      },
    };

    await dispatch(
      updateDressingChangeFrequency({
        patientId,
        assessmentId,
        interventionId,
        updateData,
        skipRefresh: true,
      })
    );
    setEditable(false);
    setFormChangesConfirm(false);
    dispatch(getDressingChangeFrequency({ patientId, assessmentId, interventionId }));
  }, [dispatch, patientId, assessmentId, interventionId, dressChange, schedules]);

  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  useEffect(() => {
    toast.dismiss("success1");
    dispatch(getDressingChangeFrequency({ patientId, assessmentId, interventionId }));
  }, [assessmentId, dispatch, interventionId, patientId]);

  useEffect(() => {
    setSchedules(buildSchedules(dressChange));
  }, [dressChange]);

  return {
    editable,
    confirmationModal,
    isLoading,
    isSaving,
    dressChange,
    isReadOnly,
    hasPermission,
    handleSubmit,
    handleEdit,
    handleCancel,
    doSubmit,
    confirmationClose,
    yesConfirmationModalClick,
    setFormChangesConfirm,
    getSchedule,
    updateSchedule,
    toggleWeekday,
  };
};
